import { useEffect, useMemo } from 'react';
import type { FloodDamageData } from '../../../types/floodDamage';
import { useTheme } from '../theme/ThemeContext';
import { floodEventDateLabel, floodEventKey, formatFloodDamagePhp, formatFloodDamagePhpExact, isValidFloodDamageRow, FLOOD_DAMAGE_COLUMN_HELP } from '../utils/floodDamagePlaces';

interface FloodDamagePopupProps {
  data: FloodDamageData | null;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
}

export default function FloodDamagePopup({
  data,
  onClose,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
}: FloodDamagePopupProps) {
  const { isDark } = useTheme();
  const row = useMemo(
    () => (data?.data ?? []).find(isValidFloodDamageRow) ?? data?.data[0],
    [data?.data]
  );

  useEffect(() => {
    if (!data) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [data, onClose]);

  if (!data || !row) return null;

  const eventName = floodEventKey(row.Event);
  const eventDate = floodEventDateLabel(row.Event);
  const stats = [
    { key: 'total' as const, value: row.TotalDamage || '—' },
    { key: 'ciLow' as const, value: row.CI_Lower || '—' },
    { key: 'ciHigh' as const, value: row.CI_Upper || '—' },
  ];

  return (
    <div
      role="dialog"
      aria-label={`${data.municipalityName} loss and damage`}
      className={`absolute left-3 bottom-3 z-20 w-[min(17.5rem,calc(100%-1.5rem))] rounded-xl border shadow-lg ${
        isDark ? 'border-gray-700 bg-gray-900/95' : 'border-gray-200 bg-white/95'
      }`}
    >
      <div className="flex items-start gap-2 px-2.5 pt-2 pb-1.5">
        <i className={`ri-flood-fill mt-0.5 text-sm ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
        <div className="min-w-0 flex-1">
          <p className={`text-xs font-semibold truncate leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {data.municipalityName}
          </p>
          <p className={`text-[10px] truncate leading-tight ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {eventName || 'Flood event'}
            {eventDate ? ` · ${eventDate}` : ''}
            {data.raster ? ' · zoomed to municipality' : ''}
          </p>
        </div>
        <div className="flex items-center shrink-0 -mr-1 -mt-0.5">
          {hasPrev && (
            <button
              type="button"
              onClick={onPrev}
              className={`p-1 rounded-md transition-[background-color,transform] duration-150 ease-out active:scale-[0.97] ${
                isDark ? 'text-gray-400 hover:bg-gray-800 hover:text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
              }`}
              title="Previous municipality"
            >
              <i className="ri-arrow-left-s-line text-base" />
            </button>
          )}
          {hasNext && (
            <button
              type="button"
              onClick={onNext}
              className={`p-1 rounded-md transition-[background-color,transform] duration-150 ease-out active:scale-[0.97] ${
                isDark ? 'text-gray-400 hover:bg-gray-800 hover:text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
              }`}
              title="Next municipality"
            >
              <i className="ri-arrow-right-s-line text-base" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className={`p-1 rounded-md transition-[background-color,color,transform] duration-150 ease-out active:scale-[0.97] ${
              isDark ? 'text-gray-400 hover:bg-gray-800 hover:text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
            }`}
            title="Close"
            aria-label="Close"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>
      </div>
      <div className={`grid grid-cols-3 gap-1 px-2.5 pb-2 pt-1 border-t ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
        {stats.map((stat) => {
          const col = FLOOD_DAMAGE_COLUMN_HELP[stat.key];
          return (
            <div key={stat.key} className="min-w-0 text-center" title={`${col.text} Exact: ${formatFloodDamagePhpExact(stat.value)}.`}>
              <span className={`inline-flex items-center justify-center gap-0.5 text-[9px] uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {col.label}
                <i className="ri-information-line text-[9px] leading-none" aria-hidden />
              </span>
              <span className={`block text-[11px] font-semibold tabular-nums leading-tight ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                {formatFloodDamagePhp(stat.value)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
